import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { tokens } from '@/theme'
import type { Capability } from '@/domain/types'
import { useCapability } from '@/api/hooks/useCapability'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { SkeletonList, ErrorPanel } from '@/components/feedback'

interface Props {
  capabilityId: number
  onBack: () => void
  /** Drilldown to a sibling/child/parent capability. */
  onSelectCapability: (capabilityId: number) => void
}

/**
 * Detail page for a single capability. Shows the capability card plus:
 * - Children list when capabilityLevel === 1 (this is a parent)
 * - Parent reference card when capabilityLevel === 2 (this is a leaf)
 *
 * The API returns `children?` or `parent?` on the detail response so we
 * don't have to issue separate queries.
 */
export function CapabilityDetailView({ capabilityId, onBack, onSelectCapability }: Props) {
  const query = useCapability(capabilityId)

  return (
    <>
      <Breadcrumb onBack={onBack}>
        <span style={{ color: tokens.ink3 }}>Capabilities</span>
        <ChevronRight size={10} color={tokens.ink4} />
        <span style={{ color: tokens.ink, fontWeight: tokens.weightDemi }}>
          {query.status === 'success' ? query.data.capabilityName : '…'}
        </span>
      </Breadcrumb>

      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `40px ${tokens.containerPadX}px`,
        }}
      >
        {query.status === 'loading' && <SkeletonList rows={1} rowHeight={160} />}
        {query.status === 'error' && <ErrorPanel error={query.error} />}
        {query.status === 'success' && (
          <>
            <CapabilityHeaderCard cap={query.data} />

            {query.data.parent && (
              <section style={{ marginTop: 32 }}>
                <SectionHeader label="Parent capability" />
                <CapabilityRow
                  cap={query.data.parent}
                  onClick={() => onSelectCapability(query.data.parent!.capabilityId)}
                />
              </section>
            )}

            {query.data.children && query.data.children.length > 0 && (
              <section style={{ marginTop: 32 }}>
                <SectionHeader label="Sub-capabilities" count={query.data.children.length} />
                <ul style={{ listStyle: 'none', display: 'grid', gap: 6, padding: 0, margin: 0 }}>
                  {query.data.children.map(child => (
                    <CapabilityRow
                      key={child.capabilityId}
                      cap={child}
                      onClick={() => onSelectCapability(child.capabilityId)}
                    />
                  ))}
                </ul>
              </section>
            )}

            {query.data.capabilityLevel === 1 &&
              (!query.data.children || query.data.children.length === 0) && (
                <section style={{ marginTop: 32 }}>
                  <SectionHeader label="Sub-capabilities" />
                  <EmptyChild label="No sub-capabilities defined yet." />
                </section>
              )}
          </>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function CapabilityHeaderCard({ cap }: { cap: Capability }) {
  return (
    <div
      style={{
        padding: '28px 32px',
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ width: 32, height: 2, background: tokens.gold }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: tokens.gold,
          }}
        >
          {cap.capabilityLevel === 1 ? 'Capability · Level 1' : 'Capability · Level 2'}
        </span>
        <span
          style={{
            fontSize: 12,
            color: tokens.ink3,
            fontWeight: tokens.weightMedium,
          }}
        >
          {cap.domainName}
        </span>
        {!cap.isActive && (
          <span
            style={{
              fontSize: 10,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: tokens.ink3,
              background: tokens.paper3,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusSm,
              padding: '2px 8px',
            }}
          >
            Inactive
          </span>
        )}
      </div>
      <h1
        style={{
          fontSize: 30,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: 0,
          marginBottom: 8,
        }}
      >
        {cap.capabilityName}
      </h1>
      {cap.capabilityDescription && (
        <p
          style={{
            fontSize: tokens.sizeBodyL,
            color: tokens.ink2,
            lineHeight: 1.55,
            maxWidth: 720,
            margin: 0,
          }}
        >
          {cap.capabilityDescription}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          gap: 32,
          marginTop: 24,
          paddingTop: 20,
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        <Stat label="Initiatives" value={cap.initiativeCount} />
        <Stat label="Sort order" value={cap.sortOrder} />
        <Stat label="Last modified" value={formatDate(cap.modifiedAt)} />
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 18,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: tokens.weightMedium,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: tokens.ink3,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  )
}

function SectionHeader({ label, count }: { label: string; count?: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        marginBottom: 16,
        paddingBottom: 10,
        borderBottom: `1px solid ${tokens.line}`,
      }}
    >
      <h2
        style={{
          fontSize: tokens.sizeH3,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: 0,
        }}
      >
        {label}
      </h2>
      {count !== undefined && (
        <span
          style={{
            fontSize: 12,
            color: tokens.ink3,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
    </div>
  )
}

function CapabilityRow({ cap, onClick }: { cap: Capability; onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        textAlign: 'left',
        padding: '14px 18px',
        background: hover ? tokens.paper2 : tokens.paper,
        border: `1px solid ${hover ? tokens.line2 : tokens.line}`,
        borderRadius: tokens.radiusMd,
        cursor: 'pointer',
        fontFamily: tokens.fontDisplay,
        transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <LevelChip level={cap.capabilityLevel} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: tokens.weightDemi,
            color: tokens.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {cap.capabilityName}
        </div>
        {cap.capabilityDescription && (
          <div
            style={{
              fontSize: 12,
              color: tokens.ink3,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cap.capabilityDescription}
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: 12,
          color: tokens.ink3,
          fontVariantNumeric: 'tabular-nums',
          minWidth: 70,
          textAlign: 'right',
        }}
      >
        {cap.initiativeCount} init.
      </span>
      <ChevronRight size={14} strokeWidth={1.6} color={hover ? tokens.gold : tokens.ink4} />
    </button>
  )
}

function LevelChip({ level }: { level: number }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.ink3,
        background: tokens.paper3,
        border: `1px solid ${tokens.line2}`,
        borderRadius: tokens.radiusSm,
        padding: '2px 6px',
        whiteSpace: 'nowrap',
      }}
    >
      L{level}
    </span>
  )
}

function EmptyChild({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: '32px 20px',
        textAlign: 'center',
        color: tokens.ink3,
        fontFamily: tokens.fontDisplay,
        fontSize: 13,
        background: tokens.paper2,
        border: `1px dashed ${tokens.line2}`,
        borderRadius: tokens.radiusMd,
      }}
    >
      {label}
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
  } catch {
    return iso
  }
}
